import { useCallback, useState, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';
import { useMediaQuery } from '@uidotdev/usehooks';
import { AnimatePresence, motion } from 'motion/react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { isRunActive } from '@/hooks/Scenarios/runActive';

import { RootState } from '../types/graph';
import { Page } from '../util/sections';

import { NavigationBar } from '../components/NavigationBar';
import { QuestionnaireDisplay } from '../components/QuestionnaireDisplay';

import { useInvalidQuestions } from '../hooks/useInvalidQuestions';
import { QuestionnaireLayout } from '../components/QuestionnaireLayout';
import { useUpdatePage } from '../hooks/useUpdatePage';
import { useQuestionnaireNavigation } from '../context/NavigationProvider';
import { useQuestionnaireValidation } from '../hooks/useQuestionnaireValidation';
import { useUpdateQuestionnaire } from '../hooks/questionnaire.hooks';
import { useQuestionnaireStatistics } from '../hooks/useQuestionnaireStatistics';
import { useStatistics } from '../hooks/useStatistics';
import { useQuestionnairePages } from '../hooks/useQuestionnairePages';
import { useActiveQueue } from '../hooks/useActiveQueue';
// @ts-ignore
import Overflow from 'react-overflow-indicator';
import { ScrollIndicator } from '@/components/ScrollIndicator';
import { SeriousSymptomsModale } from '../components/SeriousSymptomsModale';
import { scrollToTop } from '../util/scroll';
import { useNavigate } from 'react-router-dom';
import { navigateToNextQuestion, addToTotalQueue } from '../reducers/graph';
import {
  hasAResponseInputField,
  filterOnboardingQuestions,
  isQuestionnairePageEmpty,
  responseHasValue,
  selectedResponseIsButtonAnswer,
} from '../util/questionnaire';
import { useInfoQuestionChecker } from '../hooks/useInfoQuestionChecker';
import { EmergencyWarning } from '../components/EmergencyWarning';
import { QuestionnaireSkeletonLoader } from '../components/QuestionnaireSkeletonLoader';
import { KnowledgebaseQuestion } from '@/components/KnowledgebaseQuestion';
import { useAutoSaveOnVisibilityChange } from '../hooks/useAutoSaveOnVisibilityChange';
import { useSaveLastActiveQuestion } from '@/hooks/useSaveLastActiveQuestion';
import { useQuestionTransitionVariants } from '@/hooks/useQuestionTransitionVariants';
import * as Sentry from '@sentry/react';

const ANSWER_FEEDBACK_MS = 200;

interface QuestionnaireProps {
  questionnaireLoading?: boolean;
}

export const Questionnaire = ({
  questionnaireLoading = false,
}: QuestionnaireProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const invalidQuestions = useInvalidQuestions();
  useAutoSaveOnVisibilityChange(); // Auto-save questionnaire when losing focus

  const displayFullQueue = useSelector(
    (state: RootState) => state.graph.options.DISPLAY_FULL_QUEUE,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [CTAVisible, setCTAVisible] = useState(true);
  const [CTADisabled, setCTADisabled] = useState(false);

  const activeQueue = useActiveQueue();
  const isQueueReady = useSelector(
    (state: RootState) => state.questionnaire.isQueueReady,
  );
  // Guard the empty-page auto-skip against firing before the KB API has
  // populated any questions for the current page. The fast-track runner
  // submits the introduction and lands on `/questionnaire/consultation`
  // before `useSynchronizeKB` → `useQuestions` has dispatched
  // `updateQuestions`; the CONSULTATION page's sections only contain the
  // onboarding questions (CONSULTATION_DATE, CONSULTATION_TIME) that
  // `filterOnboardingQuestions` strips out, so its displayQueue is empty
  // until the KB fetch resolves. Without this guard the auto-skip walks
  // the run forward through the app.
  const hasLoadedQuestions = useSelector(
    (state: RootState) => Object.keys(state.kb.questions).length > 0,
  );
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );

  const currentPageTitle = useSelector((state: RootState) =>
    state.graph.currentPage &&
    state.questionnaire.config &&
    state.questionnaire.config.pages[state.graph.currentPage]
      ? state.questionnaire.config.pages[state.graph.currentPage].title ?? ''
      : '',
  );

  const currentQuestionIndex = useSelector(
    (state: RootState) => state.questionnaire.currentQuestionIndex,
  );

  const { isLastPage } = useQuestionnairePages();

  const nextPage = params.page?.toUpperCase() as Page;
  useUpdatePage(nextPage);

  // filter the activeQueue if on the CONSULTATION page because questions were already answered on onboarding screens
  const displayQueue = useMemo(
    () => filterOnboardingQuestions(activeQueue, currentPage),
    [currentPage, activeQueue],
  );

  useEffect(() => {
    scrollToTop();
  }, [navigate, currentQuestionIndex]);

  const {
    navigateNext,
    // navigatePrevious removed - backward navigation now handled in NavigationProvider
    navigationDirection,
    showErrors,
    setNavigationDirection,
    isQuestionExiting,
    setIsQuestionExiting,
    hasFreshSelection,
    setHasFreshSelection,
    setMandatoryQuestionsWarning,
    isNavigatingRef,
  } = useQuestionnaireNavigation();

  const isMobile = useMediaQuery('(max-width: 639px)');
  const questionVariants = useQuestionTransitionVariants(isMobile);
  const isForward = navigationDirection === 'forward';

  const cancelCtaPollingRef = useRef<(() => void) | null>(null);
  const hasRetriedEmptyLastPageRef = useRef(false);

  // True while an auto-nav exit animation we started is still pending. Lets the
  // auto-nav effect tell apart "exit we initiated, now cancelled" (must reset
  // isQuestionExiting) from CTA-driven navigation (must be left untouched).
  const autoNavExitingRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelCtaPollingRef.current?.();
    };
  }, []);

  const recordQuestionnaireStatistics = useQuestionnaireStatistics();
  const recordStatistics = useStatistics();

  // A navigation that ends without changing question must release the exit
  // state, otherwise the skeleton stays up and the CTA ignores every click
  const abortNavigation = useCallback(() => {
    autoNavExitingRef.current = false;
    setIsQuestionExiting(false);
    setCTADisabled(false);
  }, [setIsQuestionExiting]);

  const { updateQuestionnaire, isLoading } = useUpdateQuestionnaire({
    complete: isLastPage,
    onSuccess: () => {
      // isPageEmpty covers the final submission fired by the empty-last-page
      // auto-skip, where there is no "last question" to be on.
      if (isLastPage && (isLastQuestionFromCurrentPage || isPageEmpty)) {
        recordStatistics({ section: 'SUBMISSION_SUCCESS' });
      } else {
        recordQuestionnaireStatistics();
      }
      navigateNext();
    },
    onError: (error) => {
      // The empty last page has no CTA to retry from: retry once, then move
      // on rather than strand the patient (the questionnaire stays incomplete).
      if (!isLastPage || !isPageEmpty) {
        abortNavigation();
        return;
      }
      if (!hasRetriedEmptyLastPageRef.current) {
        hasRetriedEmptyLastPageRef.current = true;
        updateQuestionnaire();
        return;
      }
      Sentry.captureMessage(
        'Empty last page submission failed twice, navigating on',
        {
          level: 'error',
          extra: { status: error?.response?.status, message: error?.message },
        },
      );
      navigateNext();
    },
  });

  const { validateAndProceed, hasInvalidQuestions, resetErrors } =
    useQuestionnaireValidation({
      onValidationSuccess: () => {
        updateQuestionnaire();
      },
      onValidationError: abortNavigation,
    });

  const {
    currentQuestionId,
    previousQuestionId,
    nextQuestionId,
    fullCurrentQuestion,
    fullPreviousQuestion,
    fullNextQuestion,
    shouldDisplayPreviousQuestion,
    isLastQuestionFromCurrentPage,
    questionIdForResponse,
  } = useInfoQuestionChecker({
    currentQuestionIndex,
    displayQueue,
  });

  const currentUserResponse = useSelector(
    (state: RootState) => state.userResponses[questionIdForResponse] ?? {},
  );

  // Centralized loading state
  const isComponentLoading = questionnaireLoading || isLoading;

  // After a short hold on the selected answer, the question leaves without
  // waiting for the next one; the skeleton stands in until it is known
  const isNavigationPending = isQuestionExiting || isLoading;
  const [isAwaitingNextQuestion, setIsAwaitingNextQuestion] = useState(false);

  useEffect(() => {
    if (!isNavigationPending) {
      setIsAwaitingNextQuestion(false);
      return;
    }
    const timer = setTimeout(
      () => setIsAwaitingNextQuestion(true),
      ANSWER_FEEDBACK_MS,
    );
    return () => clearTimeout(timer);
  }, [isNavigationPending]);

  const hasValidQuestion = useMemo(
    () =>
      displayQueue.length > 0 &&
      currentQuestionIndex >= 0 &&
      currentQuestionIndex < displayQueue.length,
    [displayQueue.length, currentQuestionIndex],
  );

  // True when the page has no displayable questions (empty or INFO-only)
  const isPageEmpty = useMemo(
    () => isQuestionnairePageEmpty(displayQueue, fullCurrentQuestion),
    [displayQueue.length, fullCurrentQuestion?.format],
  );

  // True when URL page doesn't match Redux state (changePage hasn't dispatched yet)
  const isPageTransitioning = useMemo(
    () => !!nextPage && nextPage !== currentPage,
    [nextPage, currentPage],
  );

  // Gate all content rendering — false during transitions and on pages about to be skipped
  const shouldShowContent =
    hasValidQuestion && !isPageTransitioning && !isPageEmpty;

  const isQuestionRendered = shouldShowContent && !isAwaitingNextQuestion;

  // questionIdForResponse, not currentQuestionId: on an INFO pairing both the
  // INFO block and the question below it are on screen, and the queue index
  // rests on the INFO — so currentQuestionId would name a non-question and
  // lose the last question the patient actually saw.
  useSaveLastActiveQuestion(questionIdForResponse, isQuestionRendered);

  const hasCurrentQuestionResponseSelected = useMemo(
    () => Object.values(currentUserResponse ?? {}).some(responseHasValue),
    [currentUserResponse],
  );

  // True when the patient's live selection should auto-advance: a single, clean
  // button answer (see selectedResponseIsButtonAnswer).
  const isButtonAnswerSelected = useMemo(
    () =>
      selectedResponseIsButtonAnswer(
        fullNextQuestion || fullCurrentQuestion,
        currentUserResponse,
      ),
    [fullCurrentQuestion, fullNextQuestion, currentUserResponse],
  );

  const shouldHideCTA = (() => {
    // Hide CTA when question is not loaded yet (avoids initial flicker)
    if (!fullCurrentQuestion) {
      return true;
    }

    if (!shouldShowContent) {
      return true;
    }

    if (!CTAVisible) {
      return true;
    }

    // Desktop keeps the CTA in place while the question transitions out
    if (isQuestionExiting && isMobile) {
      return true;
    }

    // Only hide CTA during loading for non-multiselect questions to be able to continue selecting while loading
    if (isComponentLoading && !fullCurrentQuestion?.multipleChoice) {
      return true;
    }

    if (
      isButtonAnswerSelected &&
      hasCurrentQuestionResponseSelected &&
      hasFreshSelection
    ) {
      return true;
    }

    // Hide CTA for required single-choice questions without response (avoids flicker)
    // Use the next question for validation if current is INFO
    const questionToCheck = fullNextQuestion || fullCurrentQuestion;
    if (
      !questionToCheck?.multipleChoice &&
      questionToCheck?.required &&
      !hasCurrentQuestionResponseSelected
    ) {
      return true;
    }

    return false;
  })();

  const shouldShowSkipButton = (() => {
    // Use the next question for validation if current is INFO
    const questionToCheck = fullNextQuestion || fullCurrentQuestion;

    if (
      questionToCheck?.format === 'INFO' ||
      questionToCheck?.responses.some(
        (response) => response.format === 'SLIDER',
      )
    ) {
      return false;
    }

    if (hasAResponseInputField(questionToCheck)) {
      const currentResponseKey = Object.keys(currentUserResponse)[0];
      const currentResponseValue = currentUserResponse[currentResponseKey];

      if (currentResponseValue !== undefined) {
        return false;
      }
    }
    return !hasCurrentQuestionResponseSelected && !questionToCheck?.required;
  })();

  const goToNextQuestion = useCallback(
    (
      freshActiveQueue?: string[],
      freshCurrentPage?: Page,
      freshNextQuestionId?: string | null,
    ) => {
      const hasInfoPair =
        freshNextQuestionId !== undefined
          ? !!freshNextQuestionId
          : !!nextQuestionId;
      if (hasInfoPair) {
        dispatch(navigateToNextQuestion());
      }
      dispatch(navigateToNextQuestion());
      dispatch(
        addToTotalQueue({
          currentPage: freshCurrentPage ?? currentPage,
          queue: freshActiveQueue ?? activeQueue,
        }),
      );
      setCTADisabled(false);
    },
    [dispatch, nextQuestionId, currentPage, activeQueue],
  );

  const handleContinue = useCallback(
    (freshActiveQueue?: string[], freshCurrentPage?: Page) => {
      if (hasInvalidQuestions) {
        validateAndProceed();
        setCTADisabled(false);
        setIsQuestionExiting(false);
        return;
      }

      dispatch(
        addToTotalQueue({
          currentPage: freshCurrentPage ?? currentPage,
          queue: freshActiveQueue ?? activeQueue,
        }),
      );
      validateAndProceed();
    },
    [
      validateAndProceed,
      hasInvalidQuestions,
      setIsQuestionExiting,
      dispatch,
      currentPage,
      activeQueue,
    ],
  );

  // Read through a ref so a queue update mid-polling doesn't recreate the
  // polling callback and restart the pending auto-navigation
  const handlersRef = useRef({ handleContinue, goToNextQuestion });
  handlersRef.current = { handleContinue, goToNextQuestion };

  // Recursive function to check if the questions are updating and navigate accordingly.
  // Returns a cancel function that stops all inner polling timeouts.
  const startNavigationWithPolling = useCallback(
    (source: 'CTA' | 'AutoNav') => {
      const startTime = Date.now();
      const TIMEOUT_MS = 5000;
      const cancelled = { current: false };

      const checkAndNavigate = () => {
        if (cancelled.current) return;

        const state = store.getState();
        const updating = state.questionnaire.areQuestionsUpdating;
        const elapsed = Date.now() - startTime;

        const navigateWithFreshState = () => {
          const freshCurrentPage = state.graph.currentPage;
          const currentPageSections =
            state.questionnaire.config?.pages[freshCurrentPage]?.sections || [];
          const freshActiveQueue = currentPageSections.flatMap(
            (section) => state.questionnaire.sectionQueues[section] || [],
          );
          const freshDisplayQueue = filterOnboardingQuestions(
            freshActiveQueue,
            freshCurrentPage,
          );
          const freshIndex = state.questionnaire.currentQuestionIndex;
          const freshCurrentQuestionId = freshDisplayQueue[freshIndex];
          const freshCurrentQuestion =
            state.kb.questions[freshCurrentQuestionId];

          const freshNextQuestionId =
            freshCurrentQuestion?.format === 'INFO' &&
            freshIndex < freshDisplayQueue.length - 1
              ? freshDisplayQueue[freshIndex + 1]
              : null;

          const isLastQuestionFromCurrentPage = freshNextQuestionId
            ? freshIndex + 1 === freshDisplayQueue.length - 1
            : freshIndex === freshDisplayQueue.length - 1;

          if (isLastQuestionFromCurrentPage) {
            handlersRef.current.handleContinue(
              freshActiveQueue,
              freshCurrentPage,
            );
          } else {
            handlersRef.current.goToNextQuestion(
              freshActiveQueue,
              freshCurrentPage,
              freshNextQuestionId,
            );
          }
        };

        if (!updating) {
          navigateWithFreshState();
        } else if (elapsed >= TIMEOUT_MS) {
          // If the questions are still updating after the timeout, send a Sentry message and navigate anyway
          const currentPageSectionsForError =
            state.questionnaire.config?.pages[state.graph.currentPage]
              ?.sections || [];
          const activeQueueForError = currentPageSectionsForError.flatMap(
            (section) => state.questionnaire.sectionQueues[section] || [],
          );
          Sentry.captureMessage(
            `${source} timeout reached: areQuestionsUpdating still true after ${TIMEOUT_MS}ms`,
            {
              level: 'warning',
              extra: {
                source,
                elapsed,
                currentQuestionId:
                  activeQueueForError[state.questionnaire.currentQuestionIndex],
                queueLength: state.questionnaire.queue.length,
                activeQueueLength: activeQueueForError.length,
              },
            },
          );
          navigateWithFreshState();
        } else {
          setTimeout(checkAndNavigate, 100);
        }
      };

      const outerTimeout = setTimeout(checkAndNavigate, ANSWER_FEEDBACK_MS);

      return () => {
        cancelled.current = true;
        clearTimeout(outerTimeout);
      };
    },
    [store],
  );

  const handleNavigateNextButton = () => {
    if (isQuestionExiting) return;

    if (CTADisabled) {
      setMandatoryQuestionsWarning(true);
      return;
    }

    // DEBUG empty motives: Log CTA click on COMPLAINTS
    if (currentPage === 'COMPLAINTS') {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'CTA button clicked',
        level: 'info',
        data: {
          isLastQuestionFromCurrentPage: isLastQuestionFromCurrentPage,
          displayQueue: displayQueue,
          activeQueue: activeQueue,
          currentQuestionId: currentQuestionId,
          currentQuestionIndex: currentQuestionIndex,
        },
      });
    }

    setHasFreshSelection(false);
    setCTADisabled(true);
    setMandatoryQuestionsWarning(false);
    setNavigationDirection('forward');

    if (isLastPage && isLastQuestionFromCurrentPage) {
      setIsQuestionExiting(true);
      handleContinue();
      return;
    }

    setIsQuestionExiting(true);
    cancelCtaPollingRef.current = startNavigationWithPolling('CTA');
  };

  // Reset fresh selection flag when question changes (navigation back/forward)
  useEffect(() => {
    setHasFreshSelection(false);
    setIsQuestionExiting(false);
    setMandatoryQuestionsWarning(false);
  }, [
    currentQuestionId,
    setHasFreshSelection,
    setIsQuestionExiting,
    setMandatoryQuestionsWarning,
  ]);

  // Auto-navigate after a fresh, clean single button selection; any other answer
  // (free text, "Autre", number, date, autocomplete, multi-select) requires a
  // user-confirmed CTA.
  useEffect(() => {
    if (displayFullQueue) return;

    if (
      isButtonAnswerSelected &&
      hasFreshSelection &&
      hasCurrentQuestionResponseSelected &&
      !isLoading
    ) {
      // Start exit animation, then poll for navigation.
      autoNavExitingRef.current = true;
      setNavigationDirection('forward');
      setIsQuestionExiting(true);
      return startNavigationWithPolling('AutoNav');
    }

    // If an auto-nav exit we started no longer applies (e.g. the patient changed
    // the selection mid-flight), the effect cleanup has already cancelled the
    // pending navigation — also clear the exit state or the question stays stuck
    // with its CTA hidden. Scoped via the ref so CTA-driven navigation is never
    // disturbed.
    if (autoNavExitingRef.current) {
      autoNavExitingRef.current = false;
      setIsQuestionExiting(false);
    }
  }, [
    isButtonAnswerSelected,
    hasFreshSelection,
    hasCurrentQuestionResponseSelected,
    isLoading,
    displayFullQueue,
    setIsQuestionExiting,
    setNavigationDirection,
    startNavigationWithPolling,
  ]);

  // Manage CTA visibility based on question state and loading
  useEffect(() => {
    // Use the next question for validation if current is INFO
    const questionForCTA = fullNextQuestion || fullCurrentQuestion;

    // Hide CTA when landing on the questionnaire (fist question or refreshed page)
    if (
      fullCurrentQuestion?.multipleChoice === false &&
      fullCurrentQuestion.required &&
      !hasCurrentQuestionResponseSelected
    ) {
      setCTAVisible(false);
      return;
    }

    // Hide CTA during auto-navigation (only when a single button answer is selected)
    if (
      hasFreshSelection &&
      hasCurrentQuestionResponseSelected &&
      !isComponentLoading &&
      isButtonAnswerSelected
    ) {
      setCTAVisible(false);
      return;
    }

    // Hide CTA during loading states for BUTTON format
    if (
      questionForCTA &&
      isComponentLoading &&
      questionForCTA.responses.some((r) => r.format === 'BUTTON') &&
      !questionForCTA.multipleChoice
    ) {
      setCTAVisible(false);
      return;
    }

    // Handle required questions without responses
    if (
      questionForCTA &&
      questionForCTA.required &&
      !hasCurrentQuestionResponseSelected
    ) {
      // For non-multiselect: disable CTA
      if (questionForCTA.multipleChoice === false) {
        setCTADisabled(true);
        return;
      }
      // For multiselect: hide CTA
      if (questionForCTA.multipleChoice === true) {
        setCTAVisible(false);
        return;
      }
    }

    // Default: Show CTA
    setCTAVisible(true);
    setCTADisabled(false);
  }, [
    hasFreshSelection,
    hasCurrentQuestionResponseSelected,
    isComponentLoading,
    isButtonAnswerSelected,
    fullCurrentQuestion,
    fullNextQuestion,
  ]);

  useEffect(() => {
    if (isPageTransitioning) {
      return;
    }
    if (isNavigatingRef.current) {
      return;
    }

    // A fast-track scenario run owns navigation while active: every
    // empty-page condition it sees is transient (the landing route change
    // rebuilds section queues even though the KB wave already delivered the
    // landing question), and skipping on that flash walks the app away from
    // the page a manual click-through rests on.
    if (isRunActive()) {
      return;
    }

    if (
      isQueueReady &&
      !isComponentLoading &&
      hasLoadedQuestions &&
      isPageEmpty &&
      navigationDirection === 'forward'
    ) {
      // Auto-skip empty pages (forward navigation only)
      // Backward navigation empty-page skipping is handled in NavigationProvider
      Sentry.captureMessage('Skipping empty questionnaire page', {
        level: 'info',
        extra: {
          displayQueue: displayQueue,
          activeQueue: activeQueue,
          isLoading: isLoading,
          questionnaireLoading: questionnaireLoading,
          fullCurrentQuestion: fullCurrentQuestion,
          navigationDirection: navigationDirection,
          currentPage: currentPage,
          currentQuestionIndex: currentQuestionIndex,
          currentQuestionId: currentQuestionId,
          previousQuestionId: previousQuestionId,
          nextQuestionId: nextQuestionId,
        },
      });

      // Set ref BEFORE navigating to prevent multiple skip triggers
      isNavigatingRef.current = true;

      if (isLastPage) {
        // Empty last page: submit with complete=true instead of skipping;
        // onSuccess of updateQuestionnaire will handle the navigateNext().
        updateQuestionnaire();
      } else {
        navigateNext();
      }
    }
  }, [
    isPageTransitioning,
    isPageEmpty,
    isQueueReady,
    isComponentLoading,
    hasLoadedQuestions,
    navigationDirection,
    navigateNext,
    isLastPage,
    updateQuestionnaire,
    // Note: navigatePrevious removed - backward skipping now in NavigationProvider
    // Note: isNavigatingRef is intentionally not in deps - we read it synchronously
    // Values below are only used for Sentry logging:
    displayQueue,
    activeQueue,
    fullCurrentQuestion,
    isLoading,
    questionnaireLoading,
    currentPage,
    currentQuestionIndex,
    currentQuestionId,
    previousQuestionId,
    nextQuestionId,
  ]);

  return (
    <QuestionnaireLayout
      bottomElement={
        !shouldHideCTA && (
          <NavigationBar
            onNext={
              displayFullQueue ? handleContinue : handleNavigateNextButton
            }
            loading={invalidQuestions.includes(questionIdForResponse)}
            label={
              isLastPage && isLastQuestionFromCurrentPage
                ? 'Continuer'
                : shouldShowSkipButton && !displayFullQueue
                ? 'Passer'
                : 'Suivant'
            }
            transparent={shouldShowSkipButton && !displayFullQueue}
            disabled={CTADisabled}
          />
        )
      }
      fullHeightWhenCTAIsHidden={shouldHideCTA}
    >
      <Overflow
        tolerance={20}
        className={`hide-scrollbar h-full ${
          !shouldHideCTA ? 'nsm:max-h-[calc(100vh-19rem)]' : ''
        } nsm:[@media(max-height:48rem)]:max-h-[calc(100vh-17rem)] w-full`}
      >
        <Overflow.Content
          className={isComponentLoading ? 'pointer-events-none' : ''}
        >
          {currentPageTitle && (
            <AnimatePresence mode="wait" initial={false}>
              <motion.p
                key={currentPageTitle}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="ml-6 text-2xs nsm:text-xs text text-purple-400 font-bold pb-3.5 tracking-[0.1em]"
              >
                {currentPageTitle.toUpperCase()}
              </motion.p>
            </AnimatePresence>
          )}
          <AnimatePresence mode="wait" initial={false} custom={isForward}>
            {isQuestionRendered ? (
              <motion.div
                key={currentQuestionId}
                data-exiting={isQuestionExiting}
                className="question-answer-feedback"
                variants={questionVariants}
                custom={isForward}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {location.pathname.includes('/questionnaire/complaints') &&
                  currentQuestionIndex === 0 && (
                    <EmergencyWarning openModal={() => setIsModalOpen(true)} />
                  )}
                <div>
                  <div>
                    {displayFullQueue ? (
                      displayQueue.map((extId: string, index: number) => (
                        <div key={extId} className="mb-8">
                          <KnowledgebaseQuestion
                            extId={extId}
                            currentPage={currentPage}
                            error={
                              showErrors && invalidQuestions.includes(extId)
                            }
                            resetErrors={resetErrors}
                            style={{ animationDelay: `${index * 100}ms` }}
                          />
                        </div>
                      ))
                    ) : (
                      <QuestionnaireDisplay
                        currentQuestionId={currentQuestionId}
                        nextQuestionId={nextQuestionId}
                        previousQuestionId={previousQuestionId}
                        fullCurrentQuestion={fullCurrentQuestion}
                        fullNextQuestion={fullNextQuestion}
                        fullPreviousQuestion={fullPreviousQuestion}
                        shouldDisplayPreviousQuestion={
                          shouldDisplayPreviousQuestion
                        }
                        currentPage={currentPage}
                        showErrors={showErrors}
                        invalidQuestions={invalidQuestions}
                        resetErrors={resetErrors}
                        setHasFreshSelection={setHasFreshSelection}
                        setIsQuestionExiting={setIsQuestionExiting}
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="skeleton"
                variants={questionVariants}
                custom={isForward}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <QuestionnaireSkeletonLoader
                  hidePageTitle={!!currentPageTitle}
                  timeoutMs={isLastPage && isLoading ? 15000 : 10000}
                  resetKey={currentPage}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Overflow.Content>
        <Overflow.Indicator direction="down">
          <div
            className={`absolute bottom-0 left-0 right-2 ${
              shouldHideCTA ? 'h-40' : 'h-20'
            } nsm:h-40 pointer-events-none bg-gradient-to-t from-[#FDFDFC]/100 to-transparent z-10`}
          />
        </Overflow.Indicator>
        <ScrollIndicator questionnaireLoading={questionnaireLoading} />
      </Overflow>
      <SeriousSymptomsModale isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
    </QuestionnaireLayout>
  );
};
